
import { SimulationConfig } from '../types';

export interface StudioRack {
  reverbNode: ConvolverNode;
  reverbGain: GainNode;
  delayL: DelayNode;
  delayR: DelayNode;
  feedbackL: GainNode;
  feedbackR: GainNode;
  wetGain: GainNode;
  channelSplitter: ChannelSplitterNode;
  channelMerger: ChannelMergerNode;
  filterNode: BiquadFilterNode;
  highPassNode: BiquadFilterNode;
  distortionNode: WaveShaperNode;
  compressorNode: DynamicsCompressorNode;
}

export class AudioRack {
  public static create(ctx: AudioContext, masterGain: GainNode): StudioRack {
    const reverbNode = ctx.createConvolver();
    const reverbGain = ctx.createGain();
    reverbGain.gain.value = 0;

    const delayL = ctx.createDelay(2.0);
    const delayR = ctx.createDelay(2.0);
    const feedbackL = ctx.createGain();
    const feedbackR = ctx.createGain();
    const wetGain = ctx.createGain();
    const channelSplitter = ctx.createChannelSplitter(2);
    const channelMerger = ctx.createChannelMerger(2);

    const filterNode = ctx.createBiquadFilter();
    filterNode.type = 'lowpass';
    filterNode.frequency.value = 20000;
    filterNode.Q.value = 1.0;

    const highPassNode = ctx.createBiquadFilter();
    highPassNode.type = 'highpass';
    highPassNode.frequency.value = 20;

    const distortionNode = ctx.createWaveShaper();
    distortionNode.curve = AudioRack.makeDistortionCurve(0);
    (distortionNode as any)._lastAmt = 0;

    const compressorNode = ctx.createDynamicsCompressor();
    compressorNode.threshold.setValueAtTime(-1, ctx.currentTime);
    compressorNode.ratio.setValueAtTime(1.5, ctx.currentTime);

    // Chain
    distortionNode.connect(filterNode);
    filterNode.connect(highPassNode);
    highPassNode.connect(compressorNode);
    compressorNode.connect(masterGain);

    // Reverb Parallel
    highPassNode.connect(reverbNode);
    reverbNode.connect(reverbGain);
    reverbGain.connect(masterGain);

    // Stereo Echo Parallel
    highPassNode.connect(channelSplitter);
    channelSplitter.connect(delayL, 0);
    delayL.connect(feedbackL);
    feedbackL.connect(delayR); 
    delayL.connect(channelMerger, 0, 0);

    channelSplitter.connect(delayR, 1);
    delayR.connect(feedbackR);
    feedbackR.connect(delayL);
    delayR.connect(channelMerger, 0, 1);

    channelMerger.connect(wetGain);
    wetGain.connect(masterGain);

    return { 
      reverbNode, reverbGain, delayL, delayR, feedbackL, feedbackR, 
      wetGain, channelSplitter, channelMerger, filterNode, highPassNode, distortionNode, compressorNode
    };
  }

  public static update(rack: StudioRack, ctx: AudioContext, masterGain: GainNode, config: SimulationConfig, isMelody: boolean) {
    const now = ctx.currentTime;
    
    // Master Volume
    const targetMasterVol = config.masterVolume !== undefined ? config.masterVolume : 1.0;
    const safeMasterVol = Math.max(0, Math.min(2, targetMasterVol)) * 1.5;
    
    const currentVol = masterGain.gain.value;
    if (Math.abs(currentVol - safeMasterVol) > 0.001) {
        masterGain.gain.setTargetAtTime(safeMasterVol, now, 0.05);
    }

    // Universal Overrides
    const noReverb = config.universalNoReverb;
    const noDist = config.universalNoDistortion;
    const noEcho = config.universalNoStereoEcho;

    const reverb = noReverb ? 0 : (isMelody ? config.audioReverbLevel : config.fxReverbLevel);
    const dist = noDist ? 0 : (isMelody ? config.audioDistortionLevel : config.fxDistortionLevel);
    const lpf = config.universalLowPassOverride ? config.universalLowPassFreq : (isMelody ? config.audioLowPassFreq : config.fxLowPassFreq);
    const q = isMelody ? config.audioLowPassResonance : config.fxLowPassResonance;
    const delayEnable = noEcho ? false : (isMelody ? config.audioEcho : config.fxEcho);
    const delayTime = isMelody ? config.audioDelayTime : config.fxDelayTime;
    const delayFb = isMelody ? config.audioDelayFeedback : config.fxDelayFeedback;
    const wet = isMelody ? config.audioEchoWet : config.fxEchoWet;

    const updateParam = (param: AudioParam, target: number) => {
        if (Math.abs(param.value - target) > 0.001) {
            param.setTargetAtTime(target, now, 0.05);
        }
    };

    updateParam(rack.reverbGain.gain, reverb);
    updateParam(rack.filterNode.frequency, lpf);
    updateParam(rack.filterNode.Q, q);
    
    updateParam(rack.highPassNode.frequency, config.audioHighPassFreq || 20);
    updateParam(rack.compressorNode.threshold, config.audioCompThreshold || -12);
    updateParam(rack.compressorNode.ratio, config.audioCompRatio || 4);

    updateParam(rack.delayL.delayTime, Math.max(0.01, delayTime));
    updateParam(rack.delayR.delayTime, Math.max(0.01, delayTime * 1.01));
    
    const fb = delayEnable ? delayFb : 0;
    updateParam(rack.feedbackL.gain, fb);
    updateParam(rack.feedbackR.gain, fb);
    updateParam(rack.wetGain.gain, delayEnable ? wet : 0);

    const lastAmt = (rack.distortionNode as any)._lastAmt ?? -1;
    if (Math.abs(dist - lastAmt) > 0.01) {
      rack.distortionNode.curve = AudioRack.makeDistortionCurve(dist * 2);
      (rack.distortionNode as any)._lastAmt = dist;
    }
  }

  public static makeDistortionCurve(amount: number) {
    const k = Math.max(0, amount);
    const n_samples = 44100;
    const curve = new Float32Array(n_samples);
    const deg = Math.PI / 180;
    for (let i = 0; i < n_samples; ++i) {
      const x = (i * 2) / n_samples - 1;
      curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
    }
    return curve;
  }

  public static reset(rack: StudioRack, ctx: AudioContext) {
    const now = ctx.currentTime;
    rack.feedbackL.gain.cancelScheduledValues(now);
    rack.feedbackL.gain.setValueAtTime(0, now);
    rack.feedbackR.gain.cancelScheduledValues(now);
    rack.feedbackR.gain.setValueAtTime(0, now);
    rack.wetGain.gain.cancelScheduledValues(now);
    rack.wetGain.gain.setValueAtTime(0, now);
    rack.reverbGain.gain.cancelScheduledValues(now);
    rack.reverbGain.gain.setValueAtTime(0, now);
  }
}
